import { useLazyQuery } from '@apollo/client';
import React, { useContext, useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import AccessControl from '../../../../AccessControl';
import { MENU_ITEMS } from '../../../../app/components/sidebar/Sidebar';
import { AppContext } from '../../../../AppContext';
import {
  MODULES,
  ORDER_STATUS,
  ORDER_TYPE,
  PERMISSIONS_KEY,
  RETURN_ORDER_ITEM_STATUS,
  ROUTES,
} from '../../../../common/constants';
import { getRedirectSubmenuItem } from '../../../../common/utils';
import Error404 from '../../../../Error404';
import RedirectControl from '../../../../RedirectControl';
import {
  NEW_ORDERS_COUNT_ADMIN_V2,
  NEW_RETURN_ORDER_COUNT_ADMIN,
} from './graphql/Queries';
import './new.less';
import NewAssignDelPartner from './pages/NewAssignDelPartner';
import NewConfirmOrder from './pages/NewConfirmOrder';
import NewDelivery from './pages/NewDelivery';
import NewReturn from './pages/NewReturn';

const tabs = MENU_ITEMS?.find(
  (menuItem) => menuItem?.key === MODULES?.ORDERS,
)?.subMenu?.find((menu) => menu?.key === MODULES?.NEW)?.tabKeys;

const NewOrdersWrapper = () => {
  const {
    dispatch,
    state: { permissions, orderCount },
  } = useContext(AppContext);

  const redirectArray = getRedirectSubmenuItem(permissions, tabs);

  const [newOrdersAdminCountV2] = useLazyQuery(NEW_ORDERS_COUNT_ADMIN_V2, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [newConfirmedOrdersAdminCountV2] = useLazyQuery(
    NEW_ORDERS_COUNT_ADMIN_V2,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const [newReturnOrdersAdminCountV2] = useLazyQuery(
    NEW_RETURN_ORDER_COUNT_ADMIN,
    {
      onCompleted: (response) => {
        dispatch({
          type: 'SET_ORDER_COUNT',
          data: {
            ...orderCount,
            returnOrderCount: response?.returnOrdersAdmin?.count,
            returnRequestedOrderCount:
              response?.returnOrdersAdmin?.returnRequestedOrderCount,
          },
        });
      },
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const getCountData = async () => {
    try {
      const [
        placedResponse,
        confirmedResponse,
        returnResponse,
      ] = await Promise.all([
        newOrdersAdminCountV2({
          variables: {
            filter: {
              orderType: ORDER_TYPE?.NEW,
              showPlacedOrderCount: true,
              status: [ORDER_STATUS?.PLACED?.value],
            },
          },
        }),
        newConfirmedOrdersAdminCountV2({
          variables: {
            filter: {
              orderType: ORDER_TYPE?.NEW,
              showConfirmedOrderCount: true,
              status: [
                ORDER_STATUS?.READY_TO_SHIP?.value,
                ORDER_STATUS?.CONFIRMED?.value,
              ],
            },
          },
        }),
        newReturnOrdersAdminCountV2({
          variables: {
            filter: {
              showReturnRequestedOrderCount: true,
              status: [
                RETURN_ORDER_ITEM_STATUS?.RETURN_REQUESTED?.value,
                RETURN_ORDER_ITEM_STATUS?.READY_TO_PICKUP?.value,
              ],
            },
          },
        }),
      ]);

      dispatch({
        type: 'SET_ORDER_COUNT',
        data: {
          newOrderCount: placedResponse?.data?.newOrdersAdminV2?.count ?? 0,
          placedOrderCount:
            placedResponse?.data?.newOrdersAdminV2?.placedOrderCount ?? 0,
          confirmedOrderCount:
            confirmedResponse?.data?.newOrdersAdminV2?.count ?? 0,
          remainingConfirmedOrderCount:
            confirmedResponse?.data?.newOrdersAdminV2?.confirmedOrderCount ?? 0,
          returnOrderCount: returnResponse?.data?.returnOrdersAdmin?.count ?? 0,
          returnRequestedOrderCount:
            returnResponse?.data?.returnOrdersAdmin
              ?.returnRequestedOrderCount ?? 0,
        },
      });
    } catch (error) {
      return error;
    }
  };

  useEffect(() => {
    getCountData();
  }, []);

  return (
    <Switch>
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}`}
        render={() => (
          <RedirectControl
            redirectArray={redirectArray}
            route={`${ROUTES?.ORDERS}${ROUTES?.NEW}`}
          />
        )}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.CONFIRM_ORDER}`}
        render={(props) => (
          <AccessControl permissionKey={PERMISSIONS_KEY?.ORDERS_NEW_PLACED}>
            <NewConfirmOrder {...props} />
          </AccessControl>
        )}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.ASSIGN_DELIVERY_PARTNER}`}
        render={(props) => (
          <AccessControl permissionKey={PERMISSIONS_KEY?.ORDERS_NEW_CONFIRMED}>
            <NewAssignDelPartner {...props} />
          </AccessControl>
        )}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.DELIVERY}`}
        render={(props) => (
          <AccessControl permissionKey={PERMISSIONS_KEY?.ORDERS_NEW_DELIVERY}>
            <NewDelivery {...props} />
          </AccessControl>
        )}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.RETURN}`}
        render={(props) => (
          <AccessControl permissionKey={PERMISSIONS_KEY?.ORDERS_NEW_RETURN}>
            <NewReturn {...props} />
          </AccessControl>
        )}
      />
      <Route path="*" exact component={Error404} />
    </Switch>
  );
};

export default NewOrdersWrapper;
